import { useEffect, useState } from 'react';
import imgTexturePanel from '../../../assets/hero_image.png';
import { useInView } from '../../../hooks/useInView';
import { sistemasManutencao, urlDes } from '../../../environment/environment';
import { useBackEndRequests } from '../../backEndRequests';
import styles from './HeroLogado.module.scss';

const HEALTH_DIEF = '/dief-cfem/api/public/actuator/health';
const HEALTH_ARRECADACAO = '/arrecadacao/api/public/actuator/health';
const HEALTH_DISTRIBUICAO = '/pgrm-distribuicao/api/public/actuator/health';

type SystemStatus = 'ok' | 'maintenance' | 'partial' | 'loading' | 'hidden';

function resolveUrl(path: string): string {
  if (window.location.hostname === 'localhost') {
    return `${urlDes}${path}`;
  }
  return path;
}

async function fetchHealth(url: string): Promise<'ok' | 'partial' | 'hidden'> {
  try {
    const res = await fetch(resolveUrl(url), {
      signal: AbortSignal.timeout(4000),
    });
    if (res.status === 401 || res.status === 403) return 'hidden';
    const json = await res.json();
    return json?.status === 'UP' ? 'ok' : 'partial';
  } catch {
    return 'partial';
  }
}

interface HeroLogadoProps {
  readonly userName?: string;
}

export function HeroLogado({ userName = 'Usuário' }: HeroLogadoProps) {
  const [ref, inView] = useInView<HTMLDivElement>();

  const diefEnvDown = sistemasManutencao.isDiefManutencao === 'true';
  const arrecadacaoEnvDown =
    sistemasManutencao.isArrecadacaoManutencao === 'true';

  const [diefStatus, setDiefStatus] = useState<SystemStatus>(
    diefEnvDown ? 'maintenance' : 'loading',
  );
  const [arrecadacaoStatus, setArrecadacaoStatus] = useState<SystemStatus>(
    arrecadacaoEnvDown ? 'maintenance' : 'loading',
  );
  const [distribuicaoStatus, setDistribuicaoStatus] =
    useState<SystemStatus>('hidden');

  const { getIsDistribuidorAutorizado } = useBackEndRequests();

  useEffect(() => {
    // manutenção (env) tem precedência: sobrescreve qualquer resultado do health check
    fetchHealth(HEALTH_DIEF).then((r) =>
      setDiefStatus(diefEnvDown ? 'maintenance' : r),
    );
    fetchHealth(HEALTH_ARRECADACAO).then((r) =>
      setArrecadacaoStatus(arrecadacaoEnvDown ? 'maintenance' : r),
    );

    let ativo = true;
    getIsDistribuidorAutorizado()
      .then((autorizado) => {
        if (!ativo || !autorizado) return;
        setDistribuicaoStatus('loading');
        fetchHealth(HEALTH_DISTRIBUICAO).then((r) => {
          if (ativo) setDistribuicaoStatus(r);
        });
      })
      .catch(() => {});

    return () => {
      ativo = false;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diefEnvDown, arrecadacaoEnvDown]);

  const allStatuses = [diefStatus, arrecadacaoStatus, distribuicaoStatus];
  const visibleStatuses = allStatuses.filter((s) => s !== 'hidden');
  const allOk = visibleStatuses.every((s) => s === 'ok');
  const anyLoading = visibleStatuses.some((s) => s === 'loading');

  const statusLabel = (status: SystemStatus, name: string) => {
    if (status === 'maintenance') return `${name} em manutenção`;
    if (status === 'partial') return `${name} parcialmente indisponível`;
    if (status === 'ok') return `${name} disponível`;
    return null;
  };

  const renderStatusContent = () => {
    if (anyLoading) {
      return (
        <>
          <span className={styles.dotLoading} />
          <p className={styles.statusLabel}>Verificando sistemas...</p>
        </>
      );
    }

    if (allOk) {
      return (
        <>
          <span className={styles.dot} />
          <p className={styles.statusLabel}>Sistemas Disponíveis</p>
        </>
      );
    }

    const items = [
      { status: diefStatus, name: 'DIEF-CFEM' },
      { status: arrecadacaoStatus, name: 'Arrecadação' },
      { status: distribuicaoStatus, name: 'Distribuição' },
    ];

    return (
      <div className={styles.alerts}>
        {items.map(({ status, name }) => {
          const label = statusLabel(status, name);
          if (!label) return null;
          const isOk = status === 'ok';
          return (
            <div
              key={name}
              className={isOk ? styles.alertItemOk : styles.alertItem}
            >
              <i
                className={
                  isOk ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'
                }
                aria-hidden='true'
              />
              <p className={styles.alertLabel}>{label}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.hero} data-name='hero-logado'>
      <div className={styles.texturePanel}>
        <div aria-hidden className={styles.bg}>
          <img alt='' className={styles.bgImg} src={imgTexturePanel} />
          <div className={styles.tint} />
        </div>
      </div>

      <div
        ref={ref}
        className={`${styles.contentPanel} ${inView ? 'inView' : ''}`}
      >
        <p className={styles.title}>
          Olá, {userName}. Tudo pronto para começar?
        </p>
        <p className={styles.subtitle}>
          Gerencie suas declarações na DIEF, realize pagamentos e monitore a
          CFEM em um só lugar. Visualize as últimas atualizações do sistema.
        </p>

        <div className={styles.statusRow}>{renderStatusContent()}</div>
      </div>
    </div>
  );
}
